import { useUiStore } from '@/features/settings/ui-store.js';
import { resetChatStreamGuard } from '@/integration/services/integration-api.js';
import type { QuickActionPref } from '@/lib/api/quick-actions.js';
import { notify } from '@/lib/notify.js';
import { stageLauncherSubmit } from '@/ui/chat/launcher-submit.js';

import { launchableFiles, referenceChip } from './quick-action-files.js';

const NOTHING_TO_SEND =
  'Nothing to send — this card has no prompt, and its skills and files are not available here.';

/** What a click did: sent the card, put it in the composer, or nothing (an empty card). */
export type QuickActionLaunch = 'sent' | 'composed' | 'nothing';

/**
 * Runs a card from Home in the selected workspace.
 *
 * `send` stages it for `/chats` the way the Home composer stages a submit, and the caller navigates
 * on `'sent'`. `compose` puts the text, the skill chips and the file chips into the Home composer
 * through the same restore path a failed launch uses, so the person can finish an unfinished prompt
 * ("Draft an email about ") before anything reaches the agent.
 *
 * A card may have no prompt: a skill or a file alone is sent the way the composer sends a picked
 * skill with no text. When nothing is left to send — no prompt, and its skills and files are gone or
 * belong elsewhere — the click says so instead of doing nothing.
 *
 * A configured start page supplies `send` to attach its optional reference before navigation.
 *
 * Only the server-resolved `skills` are used, never the stored `skill_ids`, so a stale id cannot make
 * the send fail after navigation. Files picked in another workspace are left out, with one toast:
 * a reference opens only inside its own workspace.
 */
export function launchQuickAction(
  action: Pick<
    QuickActionPref,
    'prompt' | 'skills' | 'attachments' | 'launch_mode'
  >,
  send: typeof stageLauncherSubmit = stageLauncherSubmit,
): QuickActionLaunch {
  const state = useUiStore.getState();
  const { attachments, skipped } = launchableFiles(
    action.attachments,
    state.selectedWorkspaceId,
  );
  const skillIds = action.skills.map((skill) => skill.id);
  if (
    action.prompt.trim() === '' &&
    skillIds.length === 0 &&
    attachments.length === 0
  ) {
    notify.warning(NOTHING_TO_SEND);
    return 'nothing';
  }
  if (skipped > 0) {
    notify.info(
      `${skipped} ${skipped === 1 ? 'file' : 'files'} on this card ${skipped === 1 ? 'belongs' : 'belong'} to another workspace and ${skipped === 1 ? 'was' : 'were'} left out.`,
    );
  }
  if (action.launch_mode === 'compose') {
    state.setComposerPrefillAttachments(attachments);
    state.setComposerPrefillSkillIds(skillIds);
    state.setComposerPrefillRestoresDraft(true);
    state.setComposerPrefill(action.prompt);
    return 'composed';
  }
  resetChatStreamGuard();
  if (send(action.prompt, attachments.map(referenceChip), skillIds)) {
    return 'sent';
  }
  notify.warning(NOTHING_TO_SEND);
  return 'nothing';
}
